import { addSerialNumberToCsv } from "../lib/csv";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Download,
  Search,
  ChevronUp,
  ChevronDown,
} from "lucide-react";
import ERPAnalyticsHeader from "../components/ERPAnalyticsHeader";
import { api } from "../lib/api";
import { formatDateValue } from "../lib/dateFormat";

const STATUS_OPTIONS = [
  { value: "all", label: "All" },
  { value: "received", label: "Received" },
  { value: "pending", label: "Pending" },
];

const PAGE_SIZE_OPTIONS = [25, 50, 100, 250, 500];

function safeDate(value) {
  if (!value) return "—";
  return formatDateValue(value) || "—";
}

function csvEscape(value) {
  const s = value === null || value === undefined ? "" : String(value);
  return `"${s.replace(/"/g, '""')}"`;
}

function badgeClass(status) {
  if (status === "Received") {
    return "bg-emerald-50 text-emerald-700 border-emerald-200";
  }
  return "bg-amber-50 text-amber-700 border-amber-200";
}

export default function PaymentsReceivedPendingDetail() {
  const nav = useNavigate();
  const location = useLocation();

  const params = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const fromDate = params.get("from") || "";
  const toDate = params.get("to") || "";
  const initialStatus = params.get("status") || "all";

  const [status, setStatus] = useState(initialStatus);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [meta, setMeta] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(1);

  const [sort, setSort] = useState({
    field: "CInvDate",
    direction: "desc",
  });

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        setError("");

        const response = await api.post(
          "/ai/ceo-chat/payments-received-pending/detail",
          {
            from_date: fromDate || undefined,
            to_date: toDate || undefined,
            status,
          },
          { timeout: 120000 }
        );

        const data = response?.data ?? response;

        if (!cancelled) {
          setRows(Array.isArray(data?.rows) ? data.rows : []);
          setSummary(data?.summary || null);
          setMeta(data || null);
        }
      } catch (e) {
        if (!cancelled) {
          setRows([]);
          setSummary(null);
          setMeta(null);
          setError(
            e?.response?.data?.detail ||
              e?.message ||
              "Payments Received / Pending details could not be loaded safely."
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [fromDate, toDate, status]);

  useEffect(() => {
    setPage(1);
  }, [search, status, pageSize, sort]);

  const searchedRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;

    return rows.filter((row) =>
      Object.values(row || {}).some((value) =>
        String(value ?? "").toLowerCase().includes(q)
      )
    );
  }, [rows, search]);

  const sortedRows = useMemo(() => {
    const copy = [...searchedRows];
    if (!sort.field) return copy;

    copy.sort((a, b) => {
      const result = String(a?.[sort.field] ?? "").localeCompare(
        String(b?.[sort.field] ?? ""),
        undefined,
        { numeric: true }
      );
      return sort.direction === "desc" ? -result : result;
    });

    return copy;
  }, [searchedRows, sort]);

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / pageSize));
  const currentPage = Math.min(page, totalPages);

  const pagedRows = sortedRows.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const changeSort = (field) => {
    setSort((current) => ({
      field,
      direction:
        current.field === field && current.direction === "asc"
          ? "desc"
          : "asc",
    }));
  };

  const applyStatus = (value) => {
    setStatus(value);

    const next = new URLSearchParams(location.search);
    next.set("scope", "payments");
    next.set("status", value);

    nav(
      `/ceo-command-center/payments-received-pending?${next.toString()}`,
      { replace: true }
    );
  };

  const exportCsv = () => {
    if (!sortedRows.length) return;

    const columns = [
      "PoID",
      "PoNo",
      "PoDate",
      "CustomerName",
      "DestinationCountry",
      "CInvNo",
      "CInvDate",
      "PaymentTerms",
      "PaymentType",
      "CollectionStatus",
      "DueDate",
      "TransportedDate",
      "PaymentState",
      "UtilizedValue",
    ];

    const csv = [
      columns.map(csvEscape).join(","),
      ...sortedRows.map((row) =>
        columns.map((column) => csvEscape(row?.[column])).join(",")
      ),
    ].join("\n");

    const blob = new Blob([addSerialNumberToCsv(csv)], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");

    a.href = url;
    a.download = `payments-received-pending-${status}.csv`;

    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const columns = [
    ["PoNo", "PO No"],
    ["PoDate", "PO Date"],
    ["CustomerName", "Consignee"],
    ["DestinationCountry", "Country"],
    ["CInvNo", "Invoice"],
    ["CInvDate", "Invoice Date"],
    ["TransportedDate", "Transported"],
    ["DueDate", "Due"],
    ["PaymentTerms", "Payment Terms"],
    ["CollectionStatus", "ERP Status"],
    ["PaymentState", "Payment"],
    ["UtilizedValue", "Collected"],
  ];
  const NUMERIC = new Set(["UtilizedValue"]);
  const effectiveFrom = meta?.date_range?.from_date || fromDate || "";
  const effectiveTo = meta?.date_range?.to_date || toDate || "";

  return (
    <div className="erp-home-shell ceo-analytics-shell">
      <ERPAnalyticsHeader />

      <main className="erp-content ceo-analytics-content">
        <div className="ceo-erp-content space-y-5">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <button
                type="button"
                onClick={() => nav("/ceo-command-center?tab=commercial")}
                className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 mb-3"
              >
                <ArrowLeft size={16} />
                Back to Commercial
              </button>

              <h1 className="text-2xl font-semibold text-slate-900">
                Payments Received / Pending
              </h1>

              <p className="text-sm text-slate-500 mt-1">
                Payment status of shipped orders by invoice date: received or still pending.
              </p>
            </div>

            <button
              type="button"
              onClick={exportCsv}
              disabled={!sortedRows.length}
              className="inline-flex items-center gap-2 rounded-xl border px-4 py-2 text-sm font-medium bg-white disabled:opacity-50"
              style={{ borderColor: "var(--c-border)" }}
            >
              <Download size={16} />
              Export CSV
            </button>
          </div>

          <div className="flex flex-wrap gap-2">
            {STATUS_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => applyStatus(option.value)}
                className={`px-4 py-2 rounded-xl border text-sm font-medium ${
                  status === option.value
                    ? "bg-slate-900 text-white border-slate-900"
                    : "bg-white text-slate-700"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {[
              {
                label: "Eligible POs",
                value: summary?.total_count ?? 0,
                note: "Shipped orders in the selected period",
              },
              {
                label: "Received",
                value: summary?.received_count ?? 0,
                note: `${summary?.received_pct ?? 0}% of eligible POs`,
              },
              {
                label: "Pending",
                value: summary?.pending_count ?? 0,
                note: `${summary?.pending_pct ?? 0}% of eligible POs`,
              },
              {
                label: "Realised Value",
                value: Number(summary?.realized_value ?? 0).toLocaleString(
                  undefined,
                  { maximumFractionDigits: 2 }
                ),
                note: "Collected / utilized value on received POs",
              },
            ].map((card) => (
              <div
                key={card.label}
                className="bg-white border rounded-2xl p-4"
                style={{ borderColor: "var(--c-border)" }}
              >
                <div className="text-xs uppercase tracking-wide font-semibold text-slate-500">
                  {card.label}
                </div>
                <div className="text-3xl font-semibold text-slate-900 mt-2">
                  {card.value}
                </div>
                <div className="text-xs text-slate-500 mt-2">
                  {card.note}
                </div>
              </div>
            ))}
          </div>

          <div
            className="bg-white border rounded-2xl p-4 flex flex-wrap items-center justify-between gap-3"
            style={{ borderColor: "var(--c-border)" }}
          >
            <div className="text-sm text-slate-600">
              {formatDateValue(effectiveFrom) || "—"} → {formatDateValue(effectiveTo) || "—"}{!fromDate && !toDate ? " (current financial year)" : ""}
            </div>
          </div>

          {loading ? (
            <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-500 shadow-sm">
              Loading payment realization details...
            </div>
          ) : error ? (
            <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">
              {error}
            </div>
          ) : (
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-slate-900">Payment records</div>
                  <div className="mt-1 text-xs text-slate-500">
                    {sortedRows.length
                      ? `${sortedRows.length.toLocaleString()} PO(s) · one row per eligible PO`
                      : "No payment records found for this scope."}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search PO, consignee, invoice, status"
                    className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  />
                  <select
                    value={pageSize}
                    onChange={(e) => setPageSize(Number(e.target.value))}
                    className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                  >
                    {PAGE_SIZE_OPTIONS.map((size) => (
                      <option key={size} value={size}>
                        {size} rows
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mt-3 overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                      {columns.map(([field, label]) => (
                        <th
                          key={field}
                          onClick={() => changeSort(field)}
                          className={`cursor-pointer select-none py-2 pr-3 ${NUMERIC.has(field) ? "text-right" : ""}`}
                          title={`Sort by ${label}`}
                        >
                          <span className="inline-flex items-center gap-1">
                            {label}
                            {sort.field === field &&
                              (sort.direction === "asc" ? (
                                <ChevronUp size={12} />
                              ) : (
                                <ChevronDown size={12} />
                              ))}
                          </span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {pagedRows.map((row, index) => (
                      <tr key={`${row.PoID}-${index}`} className="border-b border-slate-100 last:border-0">
                        {columns.map(([field]) => (
                          <td
                            key={field}
                            className={`py-2 pr-3 ${
                              field === "PoNo"
                                ? "font-medium text-slate-800"
                                : field === "CustomerName"
                                  ? "text-slate-700"
                                  : NUMERIC.has(field)
                                    ? "text-right text-slate-800"
                                    : "text-slate-600"
                            } ${field === "PaymentTerms" ? "max-w-[260px] truncate" : ""}`}
                            title={field === "PaymentTerms" ? String(row?.[field] ?? "") : undefined}
                          >
                            {field === "PaymentState" ? (
                              <span className={`rounded-full border px-2 py-0.5 text-xs ${badgeClass(row?.[field])}`}>
                                {row?.[field] || "—"}
                              </span>
                            ) : ["PoDate", "CInvDate", "DueDate", "TransportedDate"].includes(field) ? (
                              safeDate(row?.[field])
                            ) : field === "UtilizedValue" ? (
                              Number(row?.[field] ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 })
                            ) : (
                              row?.[field] || "—"
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mt-3 flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-3 text-sm">
                <div className="text-slate-500">
                  {sortedRows.length
                    ? `Showing ${(currentPage - 1) * pageSize + 1}-${Math.min(
                        currentPage * pageSize,
                        sortedRows.length
                      )} of ${sortedRows.length}`
                    : "0 records"}
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    disabled={currentPage <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40"
                  >
                    Previous
                  </button>

                  <span className="text-slate-600">
                    Page {currentPage} of {totalPages}
                  </span>

                  <button
                    type="button"
                    disabled={currentPage >= totalPages}
                    onClick={() =>
                      setPage((p) => Math.min(totalPages, p + 1))
                    }
                    className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}

        </div>
      </main>
    </div>
  );
}
